import { addDays, subDays, startOfDay, startOfWeek, startOfMonth } from 'date-fns';
import { getLeadsWithRdv } from '../lib/supabase/leadsService';

// Dates pour la démonstration
const today = startOfDay(new Date());
const yesterday = subDays(today, 1);
const lastWeek = subDays(today, 7);
const lastMonth = subDays(today, 30);

// Fonction pour générer des dates aléatoires dans une plage
const randomDate = (start: Date, end: Date) => {
  return new Date(start.getTime() + Math.random() * (end.getTime() - start.getTime()));
};

// Données de l'entonnoir avec dates
export const funnelData = [
  {
    id: "Mail Initial",
    value: 0,
    rdv: 0,
    refus: 0,
    width: 100,
    date: randomDate(lastMonth, today),
  },
  {
    id: "Relance Mail 1",
    value: 0,
    rdv: 0,
    refus: 0,
    width: 90,
    date: randomDate(lastMonth, today),
  },
  {
    id: "Relance Mail 2",
    value: 0,
    rdv: 0,
    refus: 0,
    width: 80,
    date: randomDate(lastWeek, today),
  },
  {
    id: "LinkedIn Initial",
    value: 0,
    rdv: 0,
    refus: 0,
    width: 70,
    date: randomDate(lastWeek, today),
  },
  {
    id: "Relance LinkedIn 1",
    value: 0,
    rdv: 0,
    refus: 0,
    width: 60,
    date: randomDate(lastWeek, yesterday),
  },
  {
    id: "Relance LinkedIn 2",
    value: 0,
    rdv: 0,
    refus: 0,
    width: 50,
    date: randomDate(yesterday, today),
  },
  {
    id: "Appel Initial",
    value: 0,
    rdv: 0,
    refus: 0,
    width: 40,
    date: today,
  },
  {
    id: "Relance Appel 1",
    value: 0,
    rdv: 0,
    refus: 0,
    width: 30,
    date: today,
  },
  {
    id: "Relance Appel 2",
    value: 0,
    rdv: 0,
    refus: 0,
    width: 20,
    date: today,
  },
  {
    id: "NRP Final",
    value: 0,
    rdv: 0,
    refus: 0,
    width: 15,
    isNRP: true,
    date: today,
  },
];

// Données du tableau avec dates
export const tableData = [
  { etape: "Mail Initial", mailsEnvoyes: 0, rdvPris: 0, refus: 0, date: randomDate(lastMonth, today) },
  { etape: "Relance Mail 1", mailsEnvoyes: 0, rdvPris: 0, refus: 0, date: randomDate(lastMonth, today) },
  { etape: "Relance Mail 2", mailsEnvoyes: 0, rdvPris: 0, refus: 0, date: randomDate(lastWeek, today) },
  { etape: "LinkedIn Initial", mailsEnvoyes: 0, rdvPris: 0, refus: 0, date: randomDate(lastWeek, today) },
  { etape: "Relance LinkedIn 1", mailsEnvoyes: 0, rdvPris: 0, refus: 0, date: randomDate(lastWeek, yesterday) },
  { etape: "Relance LinkedIn 2", mailsEnvoyes: 0, rdvPris: 0, refus: 0, date: randomDate(yesterday, today) },
  { etape: "Appel Initial", mailsEnvoyes: 0, rdvPris: 0, refus: 0, date: today },
  { etape: "Relance Appel 1", mailsEnvoyes: 0, rdvPris: 0, refus: 0, date: today },
  { etape: "Relance Appel 2", mailsEnvoyes: 0, rdvPris: 0, refus: 0, date: today },
  { etape: "NRP Final", mailsEnvoyes: 0, rdvPris: 0, refus: 0, date: today },
];

// Données des KPI
export const kpiData = {
  rdvPris: 37,
  mer: 85,
  mailsEnvoyes: 217,
  messagesLinkedIn: 59,
  appelsConnectes: 7,
};

// Fonction pour filtrer les données par période
export const filterDataByPeriod = (
  data: any[],
  period: 'tous' | 'jour' | 'semaine' | 'mois' | 'personnalise',
  startDate: Date | null = null,
  endDate: Date | null = null
) => {
  const now = new Date();
  const startOfToday = startOfDay(now);
  const startOfThisWeek = startOfWeek(now, { weekStartsOn: 1 }); // Lundi
  const startOfThisMonth = startOfMonth(now);

  return data.filter(item => {
    const itemDate = item.date instanceof Date ? item.date : new Date(item.date);
    
    switch (period) {
      case 'jour':
        return itemDate >= startOfToday;
      case 'semaine':
        return itemDate >= startOfThisWeek;
      case 'mois':
        return itemDate >= startOfThisMonth;
      case 'personnalise':
        if (startDate && endDate) {
          const start = startOfDay(startDate);
          const end = startOfDay(new Date(endDate.setHours(23, 59, 59, 999)));
          return itemDate >= start && itemDate <= end;
        }
        return true;
      default:
        return true; // 'tous'
    }
  });
};

// Fonction pour calculer les KPI filtrés
export const calculateFilteredKPI = async (
  period: 'tous' | 'jour' | 'semaine' | 'mois' | 'personnalise',
  startDate: Date | null = null,
  endDate: Date | null = null
) => {
  const filteredTable = filterDataByPeriod(tableData, period, startDate, endDate);
  
  // Récupérer le nombre de RDV depuis Supabase
  let rdvCount = 0;
  try {
    const { data } = await getLeadsWithRdv();
    if (data) {
      let rdvLeads = data;
      
      // Filtrer par période si nécessaire
      if (period !== 'tous') {
        const now = new Date();
        let dateRange: { start: Date; end: Date };
        
        switch (period) {
          case 'jour':
            dateRange = {
              start: new Date(now.setHours(0, 0, 0, 0)),
              end: new Date(now.setHours(23, 59, 59, 999)),
            };
            break;
          case 'semaine':
            const day = now.getDay();
            const diff = now.getDate() - day + (day === 0 ? -6 : 1);
            dateRange = {
              start: new Date(new Date(now.setDate(diff)).setHours(0, 0, 0, 0)),
              end: new Date(new Date(now.setDate(diff + 6)).setHours(23, 59, 59, 999)),
            };
            break;
          case 'mois':
            dateRange = {
              start: new Date(now.getFullYear(), now.getMonth(), 1),
              end: new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999),
            };
            break;
          case 'personnalise':
            if (startDate && endDate) {
              dateRange = {
                start: startDate,
                end: endDate,
              };
              break;
            }
            // Si les dates ne sont pas définies, pas de filtrage
            rdvCount = rdvLeads.length;
            return {
              rdvPris: rdvCount,
              mer: Math.round(
                (filteredTable.reduce((sum, item) => sum + item.rdvPris, 0) / 
                 filteredTable.reduce((sum, item) => sum + item.mailsEnvoyes, 0)) * 100
              ) || 0,
              mailsEnvoyes: filteredTable.reduce((sum, item) => sum + item.mailsEnvoyes, 0),
              messagesLinkedIn: filteredTable
                .filter(item => item.etape.includes('LinkedIn'))
                .reduce((sum, item) => sum + item.mailsEnvoyes, 0),
              appelsConnectes: filteredTable
                .filter(item => item.etape.includes('Appel'))
                .reduce((sum, item) => sum + item.mailsEnvoyes, 0),
            };
        }
        
        // Appliquer le filtre de date
        rdvLeads = rdvLeads.filter(lead => {
          if (!lead.rdv_date) return false;
          const rdvDate = new Date(lead.rdv_date);
          return rdvDate >= dateRange.start && rdvDate <= dateRange.end;
        });
      }
      
      rdvCount = rdvLeads.length;
    }
  } catch (error) {
    console.error("Erreur lors de la récupération des RDV:", error);
  }
  
  return {
    rdvPris: rdvCount,
    mer: Math.round(
      (filteredTable.reduce((sum, item) => sum + item.rdvPris, 0) / 
       filteredTable.reduce((sum, item) => sum + item.mailsEnvoyes, 0)) * 100
    ) || 0,
    mailsEnvoyes: filteredTable.reduce((sum, item) => sum + item.mailsEnvoyes, 0),
    messagesLinkedIn: filteredTable
      .filter(item => item.etape.includes('LinkedIn'))
      .reduce((sum, item) => sum + item.mailsEnvoyes, 0),
    appelsConnectes: filteredTable
      .filter(item => item.etape.includes('Appel'))
      .reduce((sum, item) => sum + item.mailsEnvoyes, 0),
  };
}; 